/**
 * Links de captação das redes sociais: o que liga post → clique → lead no CRM.
 *
 * Cada destino de um post (uma conta numa rede) ganha o SEU link curto
 * (`/s/<slug>`). É isso que permite dizer "este lead veio do TikTok" e não só
 * "veio de um post": a mesma legenda sai com um link diferente em cada rede.
 *
 * Destino WhatsApp: o link abre a conversa com uma mensagem pronta que termina
 * com o código do link (`ref. LC-XXXXXXX`). Quando essa mensagem chega pelo
 * webhook, o código diz de qual post e de qual rede o contato veio — o
 * WhatsApp não repassa nada além do texto, então o código precisa estar nele.
 *
 * Destino site: o link redireciona com UTM (`utm_source=<rede>`), para o
 * Google Analytics do cliente separar as redes. Ali contamos só o clique.
 */

import type { SocialNetwork } from "./social-publishing";

/** Sem 0/1/i/l/o: o código é lido e às vezes digitado por gente. */
export const SLUG_ALFABETO = "23456789abcdefghjkmnpqrstuvwxyz";
export const SLUG_TAMANHO = 7;
export const SLUG_RE = new RegExp(`^[${SLUG_ALFABETO}]{${SLUG_TAMANHO}}$`);

/** `random` devolve [0, 1) — parâmetro para o servidor usar `crypto`. */
export function gerarSlug(random: () => number): string {
  let s = "";
  for (let i = 0; i < SLUG_TAMANHO; i++) s += SLUG_ALFABETO[Math.floor(random() * SLUG_ALFABETO.length)];
  return s;
}

export function codigoDeRef(slug: string): string {
  return `LC-${slug.toUpperCase()}`;
}

const CODIGO_RE = new RegExp(`\\bLC-([${SLUG_ALFABETO}]{${SLUG_TAMANHO}})\\b`, "gi");

/**
 * Códigos de link presentes numa mensagem recebida, já como slug. Barato de
 * propósito: roda em toda mensagem de WhatsApp que chega.
 */
export function extrairCodigosDeRef(texto: string | null | undefined): string[] {
  if (!texto || !/LC-/i.test(texto)) return [];
  const achados = new Set<string>();
  for (const m of texto.matchAll(CODIGO_RE)) achados.add(m[1].toLowerCase());
  return [...achados].slice(0, 3);
}

/**
 * Número para o wa.me: só dígitos, com DDI. Sem "+", é brasileiro (DDD +
 * número, com ou sem o 55). Com "+", vale o DDI que a pessoa escreveu — sem
 * isso um número dos EUA (+1 415…, 11 dígitos) virava um número de São Paulo.
 */
export function telefoneParaWaMe(telefone: string): string | null {
  const bruto = telefone.trim();
  const d = bruto.replace(/\D/g, "");
  if (bruto.startsWith("+")) return d.length >= 8 && d.length <= 15 ? d : null;
  return telefoneBrasileiro(bruto);
}

/** WhatsApp do Brasil: DDD + número (10 ou 11 dígitos), com ou sem o 55. */
export function telefoneBrasileiro(telefone: string): string | null {
  const d = telefone.replace(/\D/g, "");
  // Com "+", o DDI é o que a pessoa escreveu: só vale se for o do Brasil.
  if (telefone.trim().startsWith("+") && !d.startsWith("55")) return null;
  if (d.length === 10 || d.length === 11) return `55${d}`;
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) return d;
  return null;
}

/**
 * Link de conversa com a mensagem pronta e o código no fim.
 *
 * `https://wa.me` e não `whatsapp://` (como na Fila de Prospecção): aqui quem
 * clica está no Instagram, no TikTok ou num navegador de desktop, e o wa.me
 * cai no app quando existe e no WhatsApp Web quando não.
 */
export function montarLinkWhatsApp(telefone: string, mensagem: string, slug: string): string | null {
  const numero = telefoneParaWaMe(telefone);
  if (!numero) return null;
  const texto = `${mensagem.trim()} (ref. ${codigoDeRef(slug)})`.trim();
  return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
}

/**
 * Acrescenta UTM sem atropelar o que o cliente já pôs na URL (se ele já
 * escreveu `utm_source`, a decisão é dele).
 */
export function adicionarUtm(
  url: string,
  utm: { source: SocialNetwork; campaign: string; content?: string | null },
): string {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return url;
  }
  const definir = (k: string, v: string | null | undefined) => {
    if (v && !u.searchParams.has(k)) u.searchParams.set(k, v);
  };
  definir("utm_source", utm.source);
  definir("utm_medium", "social");
  definir("utm_campaign", utm.campaign);
  definir("utm_content", utm.content ?? null);
  return u.toString();
}

/** Nome do link em formato de campanha (`Black Friday 2026` → `black-friday-2026`). */
export function campanhaDoNome(nome: string): string {
  return (
    nome
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "social"
  );
}

/**
 * Só https: o link curto sai do NOSSO domínio, e redirecionar para http (ou
 * outro esquema) é o tipo de coisa que faz filtro de spam marcar o domínio
 * inteiro — o de todos os clientes.
 */
export function urlDeDestinoValida(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && !!u.hostname && !u.username && !u.password;
  } catch {
    return false;
  }
}

export type DestinoDoLink = "whatsapp" | "url" | "pagina";

// ─── Página de captação ──────────────────────────────────────────────────────
//
// Formulário hospedado pelo LeadConversa no próprio link curto (`/s/<slug>`):
// quem preenche entra no CRM com a rede e o post de origem — sem depender de a
// pessoa mandar a mensagem com o código no WhatsApp. Nome e WhatsApp são
// sempre pedidos (o CRM é WhatsApp-first); o resto a pessoa escolhe.

export type ModoDoCampo = "nao" | "opcional" | "obrigatorio";
export type CampoOpcional = "email" | "empresa" | "mensagem";

export type PaginaDeCaptacao = {
  titulo: string;
  descricao: string;
  botao: string;
  agradecimento: string;
  campos: Record<CampoOpcional, ModoDoCampo>;
  /** Cor do botão e dos destaques (#rrggbb). */
  cor: string;
  /** Endereço da política de privacidade do cliente (opcional, aparece no aceite). */
  politicaUrl: string | null;
  /** Depois de enviar, oferece "falar no WhatsApp agora" (usa o número do link). */
  oferecerWhatsApp: boolean;
};

export const PAGINA_PADRAO: PaginaDeCaptacao = {
  titulo: "Fale com a gente",
  descricao: "Deixe seus dados que entramos em contato pelo WhatsApp.",
  botao: "Quero ser contatado",
  agradecimento: "Recebemos seus dados! Em breve falamos com você pelo WhatsApp.",
  campos: { email: "opcional", empresa: "nao", mensagem: "opcional" },
  cor: "#0f766e",
  politicaUrl: null,
  oferecerWhatsApp: true,
};

export const PAGINA_LIMITES = { titulo: 80, descricao: 400, botao: 40, agradecimento: 300 } as const;

export const ROTULOS_DOS_CAMPOS: Record<CampoOpcional, string> = {
  email: "E-mail",
  empresa: "Empresa",
  mensagem: "Mensagem",
};

const COR_RE = /^#[0-9a-fA-F]{6}$/;

/** Problemas na configuração da página, em português. Vazio = pode salvar. */
export function validarPagina(p: PaginaDeCaptacao): string[] {
  const erros: string[] = [];
  if (!p.titulo.trim()) erros.push("Dê um título à página.");
  if (p.titulo.length > PAGINA_LIMITES.titulo) erros.push(`O título vai até ${PAGINA_LIMITES.titulo} caracteres.`);
  if (p.descricao.length > PAGINA_LIMITES.descricao) erros.push(`A descrição vai até ${PAGINA_LIMITES.descricao} caracteres.`);
  if (!p.botao.trim() || p.botao.length > PAGINA_LIMITES.botao) erros.push(`O texto do botão vai de 1 a ${PAGINA_LIMITES.botao} caracteres.`);
  if (!p.agradecimento.trim() || p.agradecimento.length > PAGINA_LIMITES.agradecimento) {
    erros.push(`A mensagem de agradecimento vai de 1 a ${PAGINA_LIMITES.agradecimento} caracteres.`);
  }
  if (!COR_RE.test(p.cor)) erros.push("A cor precisa estar no formato #rrggbb.");
  if (p.politicaUrl && !urlDeDestinoValida(p.politicaUrl)) erros.push("O endereço da política de privacidade precisa ser https://.");
  return erros;
}

export type EnvioDaPagina = {
  nome: string;
  telefone: string;
  email: string;
  empresa: string;
  mensagem: string;
  aceite: boolean;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Confere o que a pessoa preencheu. Devolve os erros por campo (para mostrar
 * ao lado de cada um) e os valores limpos.
 */
export function validarEnvio(
  pagina: PaginaDeCaptacao,
  e: EnvioDaPagina,
): { erros: Partial<Record<keyof EnvioDaPagina, string>>; limpo: EnvioDaPagina } {
  const limpo: EnvioDaPagina = {
    nome: e.nome.trim().replace(/\s+/g, " ").slice(0, 200),
    telefone: e.telefone.trim().slice(0, 32),
    email: pagina.campos.email === "nao" ? "" : e.email.trim().toLowerCase().slice(0, 200),
    empresa: pagina.campos.empresa === "nao" ? "" : e.empresa.trim().slice(0, 200),
    mensagem: pagina.campos.mensagem === "nao" ? "" : e.mensagem.trim().slice(0, 1000),
    aceite: e.aceite,
  };
  const erros: Partial<Record<keyof EnvioDaPagina, string>> = {};
  if (limpo.nome.length < 2) erros.nome = "Informe seu nome.";
  // O CRM normaliza telefone do Brasil: número de fora não teria como virar contato.
  if (!telefoneBrasileiro(limpo.telefone)) erros.telefone = "Informe um WhatsApp do Brasil com DDD.";
  if (limpo.email && !EMAIL_RE.test(limpo.email)) erros.email = "E-mail inválido.";
  for (const c of ["email", "empresa", "mensagem"] as const) {
    if (pagina.campos[c] === "obrigatorio" && !limpo[c]) erros[c] = `Preencha ${ROTULOS_DOS_CAMPOS[c].toLowerCase()}.`;
  }
  if (!limpo.aceite) erros.aceite = "É preciso concordar para enviar.";
  return { erros, limpo };
}
